import { Suspense, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RETIRED_STORAGE } from '@constants/storage';
import { AUTH_EXPIRED_EVENT, FALLBACK_META, fetchMeta, fetchSession, logout as endSession, type LoginStep, type Me, type Meta } from '@lib/api';
import { hasPermission, SessionCtx, type Session } from '@lib/auth';
import { outbox } from '@lib/outbox';
import { store } from '@lib/storage';
import { setTitleBase } from '@lib/utils/documentTitle';
import { StreamProvider } from '@lib/stream';
import { ROUTES } from '@constants/routes';
import { Mark, RouteBoundary, useLoadingGate } from '@ui';
import { EnrollGatePage, LoginPage } from './pages';
import { AppRoutes } from './routes';

/** Who is signed in (or the login), the session context and the live stream around every route. */
export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [meta, setMeta] = useState<Meta>(FALLBACK_META);
  /** a sign-in's second step still open (the page was reloaded during it): the login resumes there */
  const [resume, setResume] = useState<LoginStep | null>(null);
  /** the session ended under the person (not a sign-out): the sign-in screen says so */
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    fetchMeta().then(setMeta).catch(() => undefined);
    // GET /auth/session never answers 401: the sign-in screen opens without an error in the console
    fetchSession()
      .then((s) => {
        if (!s.signedIn) setResume(s.step ?? null);
        setMe(s.signedIn ? s.me : null);
      })
      .catch(() => setMe(null));
    // an ended session holds the field outbox (resumed when the same user signs in again)
    const expired = () => {
      outbox.pause();
      setEnded(true);
      setMe(null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, expired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, expired);
  }, []);
  useEffect(() => {
    setTitleBase(meta.appName);
  }, [meta.appName]);

  const signedIn = !!me;
  // the field outbox follows who is signed in: its own unsent items, nobody else's
  const meId = me?.id ?? null;
  useEffect(() => {
    if (meId !== null) void outbox.setUser(meId);
  }, [meId]);
  useEffect(() => {
    if (signedIn) RETIRED_STORAGE.forEach((k) => store.remove(k));
  }, [signedIn]);

  // signing out leaves the page: the next sign-in (maybe another account) starts at home, not where this one was;
  // an expired session keeps its URL, so signing in again returns there
  const navigate = useNavigate();
  const logout = useCallback(() => {
    outbox.signOut();
    endSession().finally(() => {
      navigate(ROUTES.home, { replace: true });
      setMe(null);
    });
  }, [navigate]);

  /** signed in: a step resumed after a reload is over, the next sign-in starts at the password */
  const onSignedIn = useCallback((m: Me) => {
    setResume(null);
    setEnded(false);
    setMe(m);
  }, []);

  const updateMe = useCallback((patch: Partial<Pick<Me, 'name' | 'totp'>>) => setMe((m) => (m ? { ...m, ...patch } : m)), []);
  /**
   * The gate's enrollment is done: the app opens at once (the server has it on), then the account is read again in
   * the background; a failed read keeps what is known instead of sending the person back into the gate.
   */
  const enrolled = useCallback(() => {
    setMe((m) => (m ? { ...m, totp: { enabled: true, required: m.totp?.required ?? true } } : m));
    fetchSession()
      .then((s) => s.signedIn && setMe(s.me))
      .catch(() => undefined);
  }, []);

  const booting = <BootSplash />;
  if (me === undefined) return booting;
  if (!me)
    return (
      <RouteBoundary home={ROUTES.home}>
        <Suspense fallback={booting}>
          <LoginPage meta={meta} onLogin={onSignedIn} resume={resume} sessionEnded={ended} />
        </Suspense>
      </RouteBoundary>
    );

  // the policy requires two-step verification and this session predates it: enrolling comes before anything else
  if (me.totp?.required && !me.totp.enabled)
    return (
      <RouteBoundary home={ROUTES.home}>
        <Suspense fallback={booting}>
          <EnrollGatePage meta={meta} me={me} onDone={enrolled} onSignOut={logout} />
        </Suspense>
      </RouteBoundary>
    );

  const session: Session = { me, meta, can: (p) => hasPermission(me, p), logout, updateMe };
  return (
    <SessionCtx.Provider value={session}>
      <StreamProvider>
        <AppRoutes />
      </StreamProvider>
    </SessionCtx.Provider>
  );
}

/** While the session is checked: the brand mark, only if it takes more than a moment. */
function BootSplash() {
  const visible = useLoadingGate(true);
  return (
    <div className="boot" role="status" aria-live="polite">
      <span className="sr-only">Menyiapkan…</span>
      {visible && <Mark size={40} />}
    </div>
  );
}
